import { Head, router } from '@inertiajs/react'
import type { ReactNode } from 'react'
import { BarChart, Heatmap } from '@/components/Charts'

interface TaskRow {
  id: number; title: string; points: number; stars: string
  cycle_label: string; available: boolean; goal_title: string
}
interface Props {
  name: string
  // 公开页只接收渲染所需的精简字段
  stats: { total: number; streak: number }
  daily: Record<string, number>
  badges_earned: number
  badges_total: number
  can_check_in: boolean
  uuid: string
  tasks?: TaskRow[]
}

function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="bg-white border border-[#e7ebf2] rounded-2xl px-4 py-3.5 text-center">
      <div className="text-[26px] font-extrabold leading-tight">{value}</div>
      <div className="text-[#707d90] text-[13px] mt-1">{label}</div>
    </div>
  )
}

export default function Show({ name, stats, daily, badges_earned, badges_total, can_check_in, uuid, tasks }: Props) {
  const checkIn = (taskId: number) =>
    router.post(`/u/${uuid}/checkin/${taskId}`, {}, { preserveScroll: true })

  return (
    <div className="min-h-screen px-4 py-8">
      <Head title={`${name} 的打卡`} />
      <div className="max-w-[760px] mx-auto grid gap-5">
        <header className="text-center">
          <div className="font-extrabold text-2xl bg-gradient-to-r from-[#5b7cfa] to-[#8a5bfa] bg-clip-text text-transparent">
            🎯 HabitPoint
          </div>
          <h1 className="text-[20px] font-bold mt-3 mb-0">{name} 正在坚持打卡 💪</h1>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <Stat value={stats.total} label="累计积分" />
          <Stat value={`${stats.streak} 天`} label="连续打卡" />
          <Stat value={`${badges_earned}/${badges_total}`} label="徽章" />
        </div>

        <section className="bg-white border border-[#e7ebf2] rounded-2xl p-4">
          <div className="font-bold mb-2">📈 近 30 天积分趋势</div>
          <BarChart daily={daily} />
        </section>

        <section className="bg-white border border-[#e7ebf2] rounded-2xl p-4">
          <div className="font-bold mb-2">🟩 打卡热力图</div>
          <div style={{ overflowX: 'auto' }}><Heatmap daily={daily} /></div>
        </section>

        {can_check_in && tasks && (
          <section className="bg-white border border-[#e7ebf2] rounded-2xl p-4">
            <div className="font-bold mb-3">✅ 今日可打卡</div>
            {tasks.length === 0 ? (
              <div className="text-[#707d90] text-sm">今天的任务都完成啦 🎉</div>
            ) : (
              <ul className="grid gap-2">
                {tasks.map((t) => (
                  <li key={t.id} className="flex items-center justify-between border border-[#e7ebf2] rounded-xl px-3 py-2.5">
                    <div>
                      <div className="font-medium">{t.title}</div>
                      <div className="text-[#707d90] text-xs mt-0.5">{t.goal_title} · {t.cycle_label} · {t.stars}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => checkIn(t.id)}
                      className="shrink-0 bg-[#5b7cfa] text-white text-sm font-medium rounded-lg px-3 py-1.5"
                    >
                      +{t.points} 打卡
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <footer className="text-center text-[#707d90] text-xs py-4">
          用 HabitPoint 设目标 · 拆步骤 · 完成得分
        </footer>
      </div>
    </div>
  )
}
